<template>
  <article class="invoice-create-card invoice-create-card--accent-contacts">
    <header class="invoice-create-card__head invoice-create-card__head--space">
      <h3>
        <i class="fa fa-file-text-o invoice-create-section-icon invoice-create-section-icon--contacts" aria-hidden="true"></i>
        {{ text.create.ui.infoCard.title }}
      </h3>

      <button
        type="button"
        class="invoice-create-issuer-toggle"
        @click="showIssuerInfo = !showIssuerInfo"
      >
        <i class="fa" :class="showIssuerInfo ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
        <span>{{ issuerToggleLabel }}</span>
      </button>
    </header>

    <div class="invoice-create-info-grid">
      <div class="invoice-create-field app-modal-field">
        <span>{{ text.create.ui.infoCard.fields.date }}</span>
        <VueDatePicker
          v-if="!readOnly"
          :model-value="invoiceInfo.date"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="text.create.ui.infoCard.placeholders.date"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          model-type="yyyy-MM-dd"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="generalText.common.today"
          @update:model-value="$emit('invoice-info-change', { key: 'date', value: $event || '' })"
        />
        <div v-else class="invoice-create-field-static">{{ resolvedDate }}</div>
      </div>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text.create.ui.infoCard.fields.series }}</span>
        <multiselect
          v-if="!readOnly"
          v-model="seriesModel"
          :options="effectiveSeriesOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="false"
          :allow-empty="false"
          class="app-modal-select"
          :placeholder="text.create.ui.infoCard.placeholders.series"
        />
        <div v-else class="invoice-create-field-static">{{ resolvedSeries }}</div>
      </div>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text.create.ui.infoCard.fields.folio }}</span>
        <div class="invoice-create-field-static">{{ invoiceInfo.folio || '-' }}</div>
      </div>

      <div class="invoice-create-info-row-split">
        <div class="invoice-create-field app-modal-field">
          <span>{{ text.create.ui.infoCard.fields.paymentMethod }}</span>
          <multiselect
            v-if="!readOnly"
            v-model="paymentMethodModel"
            :options="effectivePaymentMethodOptions"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="false"
            :allow-empty="false"
            class="app-modal-select"
            :placeholder="text.create.ui.infoCard.placeholders.paymentMethod"
          />
          <div v-else class="invoice-create-field-static">{{ resolvedPaymentMethod }}</div>
        </div>

        <div class="invoice-create-field app-modal-field">
          <span>{{ text.create.ui.infoCard.fields.paymentForm }}</span>
          <multiselect
            v-if="!readOnly"
            v-model="paymentFormModel"
            :options="effectivePaymentFormOptions"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="false"
            :allow-empty="false"
            class="app-modal-select"
            :placeholder="text.create.ui.infoCard.placeholders.paymentForm"
          />
          <div v-else class="invoice-create-field-static">{{ resolvedPaymentForm }}</div>
        </div>
      </div>

      <div v-if="showGlobalInvoiceToggle" class="invoice-create-info-row-global">
        <div class="invoice-create-field app-modal-field invoice-create-field--inline-toggle">
          <label
            class="app-modal-toggle invoice-create-global-toggle"
            :class="{ 'app-modal-toggle--disabled': readOnly }"
            for="invoice-global-toggle"
          >
            <input
              id="invoice-global-toggle"
              type="checkbox"
              :checked="isGlobalInvoiceEnabled"
              :disabled="readOnly"
              @change="onGlobalToggleChange"
            >
            <span>{{ text.create.ui.infoCard.fields.global }}</span>
          </label>
        </div>

        <div v-if="isGlobalInvoiceEnabled" class="invoice-create-field app-modal-field">
          <span>{{ text.create.ui.infoCard.fields.globalPeriodicity }}</span>
          <multiselect
            v-if="!readOnly"
            v-model="globalPeriodicityModel"
            :options="effectiveGlobalPeriodicityOptions"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="false"
            :allow-empty="false"
            class="app-modal-select"
            :placeholder="text.create.ui.infoCard.placeholders.globalPeriodicity"
          />
          <div v-else class="invoice-create-field-static">{{ resolvedGlobalPeriodicity }}</div>
        </div>

        <div v-if="isGlobalInvoiceEnabled" class="invoice-create-field app-modal-field">
          <span>{{ text.create.ui.infoCard.fields.globalExercise }}</span>
          <multiselect
            v-if="!readOnly"
            v-model="globalExerciseModel"
            :options="globalExerciseOptions"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="false"
            :allow-empty="false"
            class="app-modal-select"
            :placeholder="text.create.ui.infoCard.placeholders.globalExercise"
          />
          <div v-else class="invoice-create-field-static">{{ resolvedGlobalExercise }}</div>
        </div>
      </div>
    </div>

    <div v-if="showIssuerInfo" class="invoice-create-issuer-summary">
      <section class="invoice-create-issuer-summary-block">
        <h4>{{ issuerSectionTitle }}</h4>
        <dl class="invoice-create-fiscal-list">
          <div class="invoice-create-fiscal-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.rfc }}</dt>
            <dd>{{ issuerRfc }}</dd>
          </div>

          <div class="invoice-create-fiscal-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.businessName }}</dt>
            <dd>{{ issuerBusinessName }}</dd>
          </div>

          <div class="invoice-create-fiscal-row">
            <dt>{{ billingDataText.modal.form.fiscalConfiguration.fields.fiscalRegime }}</dt>
            <dd>{{ issuerTaxRegime }}</dd>
          </div>

          <div class="invoice-create-fiscal-row">
            <dt>{{ text.detail.cards.fiscalSummary.fields.fiscalResidence }}</dt>
            <dd>{{ issuerFiscalResidence }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </article>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: invoiceText } = useDomainText('invoices');
const { catalog: generalText } = useDomainText('general');
const { catalog: billingDataText } = useDomainText('billingData');

export default {
  name: 'InvoiceCreateInfoCard',
  components: {
    VueDatePicker,
  },
  props: {
    issuer: {
      type: Object,
      default: () => ({}),
    },
    invoiceInfo: {
      type: Object,
      required: true,
    },
    selectedReceiver: {
      type: Object,
      default: null,
    },
    serialOptions: {
      type: Array,
      default: () => [],
    },
    paymentMethodOptions: {
      type: Array,
      default: () => [],
    },
    paymentFormOptions: {
      type: Array,
      default: () => [],
    },
    globalInvoicePeriodicityOptions: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['invoice-info-change'],
  data() {
    return {
      text: invoiceText,
      generalText,
      billingDataText,
      showIssuerInfo: false,
    };
  },
  computed: {
    issuerSectionTitle() {
      return String(this.text?.detail?.cards?.fiscalSummary?.sections?.emitter || '').trim() || 'Emisor';
    },
    issuerToggleLabel() {
      if (this.showIssuerInfo) {
        return String(this.text?.create?.ui?.infoCard?.actions?.hideIssuerInfo || '').trim() || 'Ocultar informacion del emisor';
      }

      return String(this.text?.create?.ui?.infoCard?.actions?.showIssuerInfo || '').trim() || 'Ver informacion del emisor';
    },
    issuerBusinessName() {
      return String(this.issuer?.businessName || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    issuerRfc() {
      return String(this.issuer?.rfc || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    issuerTaxRegime() {
      return String(this.issuer?.taxRegime || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    issuerFiscalResidence() {
      return String(this.issuer?.fiscalResidence || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    effectiveSeriesOptions() {
      return Array.isArray(this.serialOptions) && this.serialOptions.length > 0
        ? this.serialOptions
        : [];
    },
    effectivePaymentMethodOptions() {
      return Array.isArray(this.paymentMethodOptions) && this.paymentMethodOptions.length > 0
        ? this.paymentMethodOptions
        : [];
    },
    effectivePaymentFormOptions() {
      return Array.isArray(this.paymentFormOptions) && this.paymentFormOptions.length > 0
        ? this.paymentFormOptions
        : [];
    },
    effectiveGlobalPeriodicityOptions() {
      return Array.isArray(this.globalInvoicePeriodicityOptions) && this.globalInvoicePeriodicityOptions.length > 0
        ? this.globalInvoicePeriodicityOptions
        : [];
    },
    receiverRfc() {
      return String(
        this.selectedReceiver?.meta?.rfc
        || this.selectedReceiver?.rfc
        || ''
      ).trim().toUpperCase();
    },
    showGlobalInvoiceToggle() {
      return this.receiverRfc === 'XAXX010101000';
    },
    isGlobalInvoiceEnabled() {
      return Number(this.invoiceInfo?.isGlobal || 0) === 1;
    },
    resolvedDate() {
      return String(this.invoiceInfo?.date || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    resolvedSeries() {
      return String(this.seriesModel?.label || this.invoiceInfo?.series || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    resolvedPaymentMethod() {
      return String(this.paymentMethodModel?.label || this.invoiceInfo?.paymentMethod || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    resolvedPaymentForm() {
      return String(this.paymentFormModel?.label || this.invoiceInfo?.paymentForm || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    defaultGlobalPeriodicityValue() {
      const monthlyOption = this.effectiveGlobalPeriodicityOptions.find((option) => String(option?.value || '') === '04');
      return String(monthlyOption?.value || this.effectiveGlobalPeriodicityOptions[0]?.value || '04').trim();
    },
    globalExerciseOptions() {
      const formatter = new Intl.DateTimeFormat('es-MX', { month: 'long' });
      const formatMonthLabel = (monthValue) => {
        const monthDate = new Date(2026, monthValue - 1, 1);
        const baseLabel = formatter.format(monthDate);
        if (baseLabel === '') {
          return '';
        }

        return baseLabel.charAt(0).toUpperCase() + baseLabel.slice(1);
      };

      const options = [];

      for (let year = 2025; year <= 2026; year += 1) {
        for (let month = 1; month <= 12; month += 1) {
          const monthCode = String(month).padStart(2, '0');
          const monthLabel = formatMonthLabel(month);
          options.push({
            value: `${year}-${monthCode}`,
            label: `${monthLabel} - ${year}`,
            meta: {
              year,
              month: monthCode,
            },
          });
        }
      }

      return options;
    },
    defaultGlobalExerciseValue() {
      const dateValue = String(this.invoiceInfo?.date || '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const fromDate = dateValue.slice(0, 7);
        const existsInOptions = this.globalExerciseOptions.some((option) => option?.value === fromDate);
        if (existsInOptions) {
          return fromDate;
        }
      }

      const now = new Date();
      const currentExercise = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const existsCurrent = this.globalExerciseOptions.some((option) => option?.value === currentExercise);
      if (existsCurrent) {
        return currentExercise;
      }

      return String(this.globalExerciseOptions[0]?.value || '').trim();
    },
    resolvedGlobalPeriodicity() {
      return String(this.globalPeriodicityModel?.label || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    resolvedGlobalExercise() {
      return String(this.globalExerciseModel?.label || '').trim() || this.text.create.ui.infoCard.emptyValue;
    },
    globalPeriodicityModel: {
      get() {
        const selected = this.effectiveGlobalPeriodicityOptions.find((option) => {
          return String(option?.value || '') === String(this.invoiceInfo?.globalPeriodicity || '').trim();
        });

        if (selected) {
          return selected;
        }

        return this.effectiveGlobalPeriodicityOptions.find((option) => {
          return String(option?.value || '') === this.defaultGlobalPeriodicityValue;
        }) || this.effectiveGlobalPeriodicityOptions[0] || null;
      },
      set(option) {
        this.$emit('invoice-info-change', { key: 'globalPeriodicity', value: String(option?.value || '').trim() });
      },
    },
    globalExerciseModel: {
      get() {
        const selectedValue = String(this.invoiceInfo?.globalExercise || '').trim();
        const selected = this.globalExerciseOptions.find((option) => String(option?.value || '') === selectedValue);
        if (selected) {
          return selected;
        }

        const fallbackValue = this.defaultGlobalExerciseValue;
        return this.globalExerciseOptions.find((option) => String(option?.value || '') === fallbackValue)
          || this.globalExerciseOptions[0]
          || null;
      },
      set(option) {
        this.$emit('invoice-info-change', { key: 'globalExercise', value: String(option?.value || '').trim() });
      },
    },
    seriesModel: {
      get() {
        const byId = this.effectiveSeriesOptions
          .find((option) => String(option?.value || '') === String(this.invoiceInfo.serialId || ''));
        if (byId) {
          return byId;
        }

        const byLabel = this.effectiveSeriesOptions
          .find((option) => String(option?.label || '') === String(this.invoiceInfo.series || ''));
        return byLabel || this.effectiveSeriesOptions[0] || null;
      },
      set(option) {
        const selected = option && typeof option === 'object' ? option : null;
        this.$emit('invoice-info-change', { key: 'serialId', value: selected?.value || '' });
        this.$emit('invoice-info-change', { key: 'series', value: selected?.label || '' });

        const nextFolio = String(selected?.meta?.folio ?? '').trim();
        if (nextFolio !== '') {
          this.$emit('invoice-info-change', { key: 'folio', value: nextFolio });
        }
      },
    },
    paymentMethodModel: {
      get() {
        return this.effectivePaymentMethodOptions
          .find((option) => String(option?.value || '') === String(this.invoiceInfo.paymentMethod || ''))
          || this.effectivePaymentMethodOptions[0]
          || null;
      },
      set(option) {
        this.$emit('invoice-info-change', { key: 'paymentMethod', value: option?.value || '' });
      },
    },
    paymentFormModel: {
      get() {
        return this.effectivePaymentFormOptions
          .find((option) => String(option?.value || '') === String(this.invoiceInfo.paymentForm || ''))
          || this.effectivePaymentFormOptions[0]
          || null;
      },
      set(option) {
        this.$emit('invoice-info-change', { key: 'paymentForm', value: option?.value || '' });
      },
    },
  },
  methods: {
    onGlobalToggleChange(event) {
      const checked = Boolean(event?.target?.checked);
      this.$emit('invoice-info-change', { key: 'isGlobal', value: checked ? 1 : 0 });

      if (!checked) {
        return;
      }

      if (String(this.invoiceInfo?.globalPeriodicity || '').trim() === '') {
        this.$emit('invoice-info-change', {
          key: 'globalPeriodicity',
          value: this.defaultGlobalPeriodicityValue,
        });
      }

      if (String(this.invoiceInfo?.globalExercise || '').trim() === '') {
        this.$emit('invoice-info-change', {
          key: 'globalExercise',
          value: this.defaultGlobalExerciseValue,
        });
      }
    },
  },
};
</script>
